import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Calculator, ArrowLeft, Edit, Send, Download,
  CheckCircle, XCircle, Clock, FileText
} from 'lucide-react'

import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import { useToast } from '../components/Toast'
import orcamentosService, { Orcamento } from '../services/orcamentos'
import apiService from '../services/api'

const OrcamentoDetailsPage: React.FC = () => {
  const { orcamentoId } = useParams<{ orcamentoId: string }>()
  const navigate = useNavigate()
  const { success, error } = useToast()

  // Buscar dados do orçamento
  const { data: orcamento, isLoading, refetch } = useQuery({
    queryKey: ['orcamento', orcamentoId],
    queryFn: () => orcamentosService.getById(Number(orcamentoId)),
    enabled: !!orcamentoId
  })

  const getStatusConfig = (status: Orcamento['status']) => {
    const configs = {
      RASCUNHO: { color: 'gray' as const, icon: FileText, label: 'Rascunho' },
      ENVIADO: { color: 'blue' as const, icon: Send, label: 'Enviado' },
      APROVADO: { color: 'green' as const, icon: CheckCircle, label: 'Aprovado' },
      REJEITADO: { color: 'red' as const, icon: XCircle, label: 'Rejeitado' },
      EXPIRADO: { color: 'orange' as const, icon: Clock, label: 'Expirado' }
    }
    return configs[status] || configs.RASCUNHO
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT')
  }

  const handleDownloadPdf = async () => {
    try {
      await apiService.generateOrcamentoPdf(Number(orcamentoId))
      success('Cupom PDF baixado com sucesso!')
    } catch (err: any) {
      error(`Erro ao baixar cupom PDF: ${err.message}`)
    }
  }

  const handleSend = async () => {
    try {
      await orcamentosService.send(Number(orcamentoId))
      success('Orçamento enviado com sucesso!')
      refetch()
    } catch (err: any) {
      error(`Erro ao enviar orçamento: ${err.message}`)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!orcamento) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Orçamento não encontrado</p>
        <Button
          variant="ghost"
          onClick={() => navigate('/orcamentos')}
          className="mt-4"
        >
          Voltar para orçamentos
        </Button>
      </div>
    )
  }

  const statusConfig = getStatusConfig(orcamento.status)
  const StatusIcon = statusConfig.icon

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/orcamentos')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Voltar
            </Button>
            <Calculator className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Orçamento {orcamento.numero}
              </h1>
              <p className="text-gray-600">
                OS: {orcamento.ordemServico.numero}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Badge
              color={statusConfig.color}
              icon={<StatusIcon className="h-3 w-3" />}
            >
              {statusConfig.label}
            </Badge>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={handleDownloadPdf}
              >
                Baixar PDF
              </Button>

              {orcamento.status === 'RASCUNHO' && (
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Send className="h-4 w-4" />}
                  onClick={handleSend}
                >
                  Enviar
                </Button>
              )}

              <Button
                variant="primary"
                size="sm"
                leftIcon={<Edit className="h-4 w-4" />}
                onClick={() => navigate(`/orcamentos/${orcamento.id}/editar`)}
              >
                Editar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 space-y-6">
        {/* Informações principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Cliente */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cliente</h3>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Nome:</span> {orcamento.cliente.nome}
              </div>
              {orcamento.cliente.email && (
                <div>
                  <span className="font-medium">Email:</span> {orcamento.cliente.email}
                </div>
              )}
              {orcamento.cliente.telefone && (
                <div>
                  <span className="font-medium">Telefone:</span> {orcamento.cliente.telefone}
                </div>
              )}
              {orcamento.cliente.nif && (
                <div>
                  <span className="font-medium">NIF:</span> {orcamento.cliente.nif}
                </div>
              )}
              {orcamento.cliente.morada && (
                <div>
                  <span className="font-medium">Morada:</span> {orcamento.cliente.morada}
                </div>
              )}
            </div>
          </div>

          {/* Equipamento */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Equipamento</h3>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Equipamento:</span>{' '}
                {orcamento.ordemServico.equipamento.modelo.marca.nome}{' '}
                {orcamento.ordemServico.equipamento.modelo.nome}
              </div>
              <div>
                <span className="font-medium">Tipo:</span>{' '}
                {orcamento.ordemServico.equipamento.modelo.tipoEquipamento.nome}
              </div>
              <div>
                <span className="font-medium">Problema:</span>{' '}
                {orcamento.ordemServico.problemaRelatado}
              </div>
            </div>
          </div>
        </div>

        {/* Detalhes do orçamento */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informações do orçamento */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações</h3>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Data:</span> {formatDate(orcamento.criadoEm)}
              </div>
              <div>
                <span className="font-medium">Válido até:</span> {formatDate(orcamento.validadeAte)}
              </div>
              <div>
                <span className="font-medium">Prazo de reparação:</span> {orcamento.prazoReparacao}
              </div>
              <div>
                <span className="font-medium">Garantia:</span> {orcamento.garantia}
              </div>
              <div>
                <span className="font-medium">Criado por:</span> {orcamento.criador.nome}
              </div>
            </div>
          </div>

          {/* Valores */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Valores</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{formatCurrency(orcamento.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>IVA ({orcamento.ivaPercentual}%):</span>
                <span>{formatCurrency(orcamento.valorIva)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(orcamento.valorTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Itens */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Itens do Orçamento</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Qtd
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Unit.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orcamento.itens.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge color={item.tipo === 'PECA' ? 'blue' : 'green'}>
                        {item.tipo === 'PECA' ? 'Peça' : 'Serviço'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{item.descricao}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantidade}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.valorUnitario)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(item.valorTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Observações */}
        {orcamento.observacoesCliente && (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Observações</h3>
            <p className="text-gray-700">{orcamento.observacoesCliente}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrcamentoDetailsPage